import React, {useEffect, useRef, useState} from 'react';
import {useLocaleKeys} from '../../../../../locale-keys/LocaleKeys';
import {DynamicStep, DynamicStepComponentProps} from '../../StepsManager/Components/DynamicStep';
import {StepHeader} from '../../StepsManager/Components/StepHeader';
import {useControllerProps} from '../../../Widget/ControllerContext';
import {StepImplementationProps, StepState} from '../../../../../types/checkoutApp.types';
import {usePaymentsApi} from '../../../../../common/components/WithPaymentsApi/WithPaymentsApi';
import {useBillingData, withBillingData} from './WithBillingData';
import {useMemberDetailsData, withMemberDetailsData} from '../../../MemberDetails/WithMemberDetailsData';
import {PaymentWidgetWrapper} from './PaymentWidgetWrapper';
import {BillingDetails} from './Components/BillingDetails/BillingDetails';
import {Divider, StatesButtonStates} from '@wix/wix-ui-tpa/cssVars';
import {UsePlaceOrderButtonState} from './UsePlaceOrderButtonState';
import {DetailsFormButtons} from '../../StepsManager/Components/DetailsFormButtons/DetailsFormButtons';
import {ADD_NEW_ADDRESS_ID} from '../../../constants';
import {useFunctionResultObservation} from '@wix/function-result-observation';
import {classes} from './PaymentAndPlaceOrderStep.st.css';
import {classes as commonClasses} from '../../../../../common/common.st.css';

import {
  BottomMobileSummary,
  BottomMobileSummaryDataHooks,
} from '../../../Summary/BottomMobileSummary/BottomMobileSummary';
import {shouldHideBillingDetailsSection} from '../../../../../domain/utils/billingDetails.utils';
import {CartModel} from '../../../../../domain/models/checkout/Cart/Cart.model';
import {PlaceOrderTitle} from './PlaceOrderTitle';
import {Checkboxes} from '../../../Checkboxes/Checkboxes';
import {ViolationsAndPlaceOrderButton} from '../../../ViolationsAndPlaceOrderButton/ViolationsAndPlaceOrderButton';
import {PaymentAndPlaceOrderSkeleton} from './PaymentAndPlaceOrderSkeleton';

export enum PaymentAndPlaceOrderDataHook {
  root = 'PaymentAndPlaceOrderDataHook.root',
  header = 'PaymentAndPlaceOrderDataHook.header',
  open = 'PaymentAndPlaceOrderDataHook.open',
  summary = 'PaymentAndPlaceOrderDataHook.summary',
  Loader = 'PaymentAndPlaceOrderDataHook.loader',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const InternalPaymentAndPlaceOrderStep = ({index}: DynamicStepComponentProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const checkboxesRef = useRef<HTMLDivElement>(null);
  const paymentWidgetRef = useRef<HTMLDivElement>(null);
  const localeKeys = useLocaleKeys();
  const {activePaymentId} = usePaymentsApi();

  const {
    checkoutStore,
    formsStore: {areFormsLoaded},
    stepsManagerStore: {stepsList, hasMovedToNextStep},
    memberStore: {isMember},
    navigationStore: {isDonationsTheme},
    checkoutSettingsStore: {checkoutContent},
    checkoutStore: {cartSummary},
    isOneColumnView,
  } = useControllerProps();

  const {isPlaceOrderButtonDisabled, cart} = checkoutStore;
  const {initForm, getBillingFormDataForSubmit, isFormValid, billingSameAsShipping, isShippingValidForBilling} =
    useBillingData();
  const {withObservation} = useFunctionResultObservation();
  const setBillingDetails = withObservation(checkoutStore, 'setBillingDetails');

  const {resetMemberDetailsState, editMode, isSetAsDefaultChecked, setEditMode, selectedAddressesServiceId} =
    useMemberDetailsData();

  const {setPlaceOrderButtonState, setShouldPlaceOrderButtonBeDisabled} = UsePlaceOrderButtonState();

  const shouldShowFormDetailsButtons =
    isMember &&
    (editMode || selectedAddressesServiceId === ADD_NEW_ADDRESS_ID || !selectedAddressesServiceId) &&
    (!billingSameAsShipping || !isShippingValidForBilling);
  const [saveBillingAddressButtonState, setSaveBillingAddressButtonState] = useState(StatesButtonStates.IDLE);
  const [isSaveBillingAddressButtonBeDisabled, setIsSaveBillingAddressButtonDisabled] = useState(false);

  const stepState = stepsList[index].state;

  const scrollToHeader = () => {
    headerRef?.current?.scrollIntoView();
  };

  const shouldShowPaymentsWidget = cartSummary && cartSummary.paymentSummary.requiresPaymentAfterGiftCard;

  useEffect(
    () => {
      if (stepState === StepState.OPEN && shouldShowPaymentsWidget) {
        if (shouldHideBillingDetailsSection(cart, activePaymentId)) {
          checkoutStore.onHideBillingDetails();
        }
        setPlaceOrderButtonState(StatesButtonStates.IDLE);
        setShouldPlaceOrderButtonBeDisabled(!areFormsLoaded || isPlaceOrderButtonDisabled);
        initForm();
        if (isMember) {
          resetMemberDetailsState();
        }
      }
      if (stepState === StepState.OPEN && hasMovedToNextStep) {
        scrollToHeader();
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */ [stepState]
  );

  useEffect(() => {
    if (stepState === StepState.OPEN && shouldHideBillingDetailsSection(cart, activePaymentId)) {
      checkoutStore.onHideBillingDetails();
    }
  }, [activePaymentId]);

  const validateAndSubmitMemberBillingDetails = async () => {
    setSaveBillingAddressButtonState(StatesButtonStates.IN_PROGRESS);
    setIsSaveBillingAddressButtonDisabled(true);
    const isBillingFormValid = await isFormValid();
    /* istanbul ignore next */
    if (isBillingFormValid) {
      await setBillingDetails({
        ...getBillingFormDataForSubmit(),
        billingAddressId: selectedAddressesServiceId ?? ADD_NEW_ADDRESS_ID,
        setAsDefault: isSetAsDefaultChecked,
      });

      setEditMode(false);
      setShouldPlaceOrderButtonBeDisabled(!areFormsLoaded || isPlaceOrderButtonDisabled);
    }

    setSaveBillingAddressButtonState(StatesButtonStates.IDLE);
    setIsSaveBillingAddressButtonDisabled(false);
  };

  const getHeaderLabel = (): string => {
    let headerLabel: string = '';
    if (shouldShowPaymentsWidget) {
      headerLabel = localeKeys.checkout.payment();
    } else {
      const reviewOrderText = isDonationsTheme
        ? localeKeys.checkout.donations.review_donation.title()
        : localeKeys.checkout.place_order.$value();
      headerLabel = checkoutContent?.reviewAndPlaceOrder ?? reviewOrderText;
    }

    return headerLabel;
  };

  return (
    <>
      <div className={commonClasses.oneColumnViewPadding}>
        <StepHeader
          index={index}
          dataHook={PaymentAndPlaceOrderDataHook.header}
          label={getHeaderLabel()}
          ref={headerRef}
        />
        {shouldShowPaymentsWidget && <PaymentWidgetWrapper stepState={stepState} paymentWidgetRef={paymentWidgetRef} />}
        {stepState === StepState.OPEN &&
          (cartSummary ? (
            <div data-hook={PaymentAndPlaceOrderDataHook.open}>
              {shouldShowPaymentsWidget && !shouldHideBillingDetailsSection(cart, activePaymentId) && (
                <>
                  <BillingDetails />
                  {shouldShowFormDetailsButtons && (
                    <DetailsFormButtons
                      onCancel={scrollToHeader}
                      onContinue={() => void validateAndSubmitMemberBillingDetails()}
                      continueButtonDisabled={isSaveBillingAddressButtonBeDisabled}
                      continueButtonState={saveBillingAddressButtonState}
                      label={localeKeys.checkout.billingAddress.save.cta()}
                    />
                  )}
                  <Divider className={classes.stepDivider} direction="horizontal" />
                </>
              )}
              <div>
                <PlaceOrderTitle />
                <Checkboxes checkboxesRef={checkboxesRef} />
              </div>
            </div>
          ) : (
            <PaymentAndPlaceOrderSkeleton dataHook={PaymentAndPlaceOrderDataHook.Loader} />
          ))}
      </div>
      {isOneColumnView && (
        <BottomMobileSummary
          dataHook={BottomMobileSummaryDataHooks.paymentAndPlaceOrderStep}
          checkboxesRef={checkboxesRef}
          paymentWidgetRef={paymentWidgetRef}
        />
      )}
      {stepState === StepState.OPEN && (
        <ViolationsAndPlaceOrderButton checkboxesRef={checkboxesRef} paymentWidgetRef={paymentWidgetRef} />
      )}
    </>
  );
};

const WrappedInternalPaymentAndPlaceOrderStep = withMemberDetailsData(
  withBillingData(InternalPaymentAndPlaceOrderStep),
  (cart: CartModel) => cart.paymentInfo,
  'validateBillingAddress'
);

export const PaymentAndPlaceOrderStep = ({index}: StepImplementationProps) => {
  return (
    <DynamicStep
      index={index!}
      dataHook={PaymentAndPlaceOrderDataHook.root}
      component={WrappedInternalPaymentAndPlaceOrderStep}
    />
  );
};
